import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../../services/apiClient'
import { useAuthStore } from '@/store/authStore'
import { supabase } from '@/lib/supabase'
import CreateUserModal from '@/components/CreateUserModal'
import BatchCreateUsersModal from '@/components/BatchCreateUsersModal'

interface User {
  id: string
  email: string
  full_name: string
  phone?: string
  status: number      // 后端的实际状态值：1=正常, 2=冻结, 3=注销
  is_active: boolean  // 兼容字段（从 status 计算得出）
  last_login_at?: string
  operator_code?: string
  operator_name?: string
  email_verified: boolean
  phone_verified: boolean
  created_at: string
  real_balance: number
  bonus_balance: number
  total_deposit: number
  total_profit: number
}

interface UsersListResponse {
  users: User[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
    has_more: boolean
  }
}

export default function UsersPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | '1' | '2' | '3'>('all')
  const [operatorFilter, setOperatorFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    has_more: false
  })
  
  // 运营商相关状态
  const [isOperator, setIsOperator] = useState(false)
  const [operatorId, setOperatorId] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showBatchCreateModal, setShowBatchCreateModal] = useState(false)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(operatorFilter && { operator_code: operatorFilter }),
        sort_by: 'created_at',
        sort_order: 'desc'
      })

      const response = await apiClient.get<UsersListResponse>(`/admin-users-list?${params}`)
      
      if (response.success && response.data) {
        setUsers(response.data.users)
        setPagination(response.data.pagination)
      } else {
        setError(response.message || '获取用户列表失败')
      }
    } catch (err) {
      setError('网络错误，请稍后重试')
      console.error('Fetch users error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
    checkIfOperator()
  }, [currentPage, searchTerm, statusFilter, operatorFilter])
  
  const checkIfOperator = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('role, id')
        .eq('id', user?.id)
        .single()
      
      if (data && data.role === 'operator') {
        setIsOperator(true)
        setOperatorId(data.id)
      }
    } catch (err) {
      console.error('Check operator error:', err)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchUsers()
  }

  const handleUserClick = (userId: string) => {
    navigate(`/users/${userId}`)
  }

  const handleStatusToggle = async (userId: string, currentStatus: number, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation() // 阻止事件冒泡，避免触发行点击
    
    // 只在正常(1)和冻结(2)之间切换
    const newStatus = currentStatus === 1 ? 2 : 1
    const newIsActive = newStatus === 1
    
    const confirmMessage = newIsActive 
      ? '确定要启用此用户吗？用户将恢复正常状态。' 
      : '确定要冻结此用户吗？冻结后用户将无法登录。'
    
    if (!confirm(confirmMessage)) {
      return
    }

    try {
      const response = await apiClient.put(`/admin-user-manage?action=status`, {
        user_id: userId,
        is_active: newIsActive,
        reason: newIsActive ? '管理员启用' : '管理员冻结'
      })

      if (response.success) {
        // 更新本地状态
        setUsers(users.map(user => 
          user.id === userId 
            ? { ...user, status: newStatus, is_active: newIsActive }
            : user
        ))
      } else {
        alert(response.message || '操作失败')
      }
    } catch (err) {
      console.error('Toggle status error:', err)
      alert('网络错误，请稍后重试')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN')
  }

  if (loading && users.length === 0) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="page-title">用户管理</h1>
          <p className="page-subtitle">
            管理平台用户账户和信息
          </p>
        </div>
        {isOperator && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              创建用户
            </button>
            <button
              onClick={() => setShowBatchCreateModal(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              批量创建
            </button>
          </div>
        )}
      </div>

      {/* 搜索和筛选 */}
      <div className="card mb-6">
        <div className="card-body">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  搜索用户
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="邮箱、姓名或手机号"
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  用户状态
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">全部状态</option>
                  <option value="1">正常</option>
                  <option value="2">冻结</option>
                  <option value="3">注销</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  运营商代码
                </label>
                <input
                  type="text"
                  value={operatorFilter}
                  onChange={(e) => setOperatorFilter(e.target.value)}
                  placeholder="运营商代码"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full btn btn-secondary"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  筛选
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {/* 用户列表 */}
      <div className="card">
        <div className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {users.map((user) => (
              <li key={user.id}>
                <div
                  onClick={() => handleUserClick(user.id)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user.full_name?.charAt(0)??'-'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {user.full_name??'-'}
                          </p>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.status === 1 
                              ? 'bg-green-100 text-green-800' 
                              : user.status === 2
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status === 1 ? '正常' : user.status === 2 ? '冻结' : '注销'}
                          </span>
                        </div>
                        <div className="flex items-center mt-1 space-x-4">
                          <p className="text-sm text-gray-500">{user.email}</p>
                          {user.phone && (
                            <p className="text-sm text-gray-500">{user.phone}</p>
                          )}
                          {user.operator_code && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {user.operator_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* 右侧：余额信息和状态切换 */}
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          真实余额: {formatCurrency(user.real_balance)}
                        </div>
                        <div className="text-sm text-green-600 font-medium">
                          赠送余额: {formatCurrency(user.bonus_balance)}
                        </div>
                        <div className="text-sm text-gray-500">
                          总充值: {formatCurrency(user.total_deposit)}
                        </div>
                        <div className="text-sm text-gray-500">
                          注册: {formatDate(user.created_at)}
                        </div>
                      </div>
                      
                      {/* 状态切换开关 - 只对正常和冻结状态有效 */}
                      {user.status !== 3 && (
                        <div 
                          className="flex flex-col items-center space-y-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={user.status === 1}
                              onChange={(e) => handleStatusToggle(user.id, user.status, e)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                          <span className="text-xs text-gray-500">
                            {user.status === 1 ? '正常' : '冻结'}
                          </span>
                        </div>
                      )}
                      
                      {/* 注销状态不显示开关 */}
                      {user.status === 3 && (
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-xs text-gray-500">已注销</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {users.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">暂无用户数据</p>
            </div>
          )}
        </div>

        {/* 分页 */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 20, pagination.total)}
                  </span>{' '}
                  条，共 <span className="font-medium">{pagination.total}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {currentPage} / {pagination.pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 创建用户模态框 */}
      {operatorId && (
        <CreateUserModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            fetchUsers()
          }}
          operatorId={operatorId}
        />
      )}
      
      {/* 批量创建用户模态框 */}
      {operatorId && (
        <BatchCreateUsersModal
          isOpen={showBatchCreateModal}
          onClose={() => setShowBatchCreateModal(false)}
          onSuccess={() => {
            setShowBatchCreateModal(false)
            fetchUsers()
          }}
          operatorId={operatorId}
        />
      )}
    </div>
  )
}